import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SocialPlatform } from '@/lib/api';
import { socialMediaApi } from '../api';
import { socialMediaQueryKeys } from '../api/queryKeys';
import { toast } from '@/components/ui/use-toast';

/**
 * Lists + connects + disconnects social-media accounts for a Holder.
 * The caller passes the Holder ID (resolved from `useHolderSwitcher`);
 * when no Holder is selected the hook stays in a loading state and
 * does not fire any requests.
 */
export function useSocialAccounts(holderId: string | null) {
  const queryClient = useQueryClient();
  const key = socialMediaQueryKeys.accounts(holderId ?? '');

  const query = useQuery({
    queryKey: key,
    queryFn: () => socialMediaApi.listSocialMediaAccounts(holderId as string),
    enabled: !!holderId,
  });

  const disconnectMutation = useMutation({
    mutationFn: (accountId: string) =>
      socialMediaApi.disconnectSocialMediaAccount(holderId as string, accountId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      toast({ title: 'Account disconnected' });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to disconnect account';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  // refetch / mutateAsync are stable references (unlike the query/mutation
  // result objects, which are new every render). Depending on them keeps these
  // callbacks stable — important because loadAccounts is an effect dependency
  // in AccountsPage's OAuth-callback handling.
  const { refetch } = query;
  const { mutateAsync: disconnectAccountMutate } = disconnectMutation;

  const loadAccounts = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const connectAccount = useCallback(
    async (platform: SocialPlatform) => {
      if (!holderId) {
        toast({
          title: 'Pick a workspace',
          description: 'Select a workspace before connecting an account.',
          variant: 'destructive',
        });
        return;
      }
      try {
        const result = await socialMediaApi.initSocialMediaOAuth(holderId, platform);
        window.location.href = result.authorizationUrl;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start account connection';
        toast({ title: 'Connection failed', description: message, variant: 'destructive' });
      }
    },
    [holderId],
  );

  const disconnectAccount = useCallback(
    async (accountId: string) => {
      if (!holderId) return;
      // Errors are surfaced via the mutation's onError toast.
      await disconnectAccountMutate(accountId).catch(() => undefined);
    },
    [holderId, disconnectAccountMutate],
  );

  return {
    accounts: query.data?.accounts ?? [],
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    loadAccounts,
    connectAccount,
    disconnectAccount,
  };
}
